import { Component, OnInit } from '@angular/core';
import { ColumnMode, SelectionType, SortType } from '@swimlane/ngx-datatable';
import { DataService } from 'src/app/data.service';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, EMPTY, firstValueFrom, retry, throwError } from 'rxjs';
import { isInt } from '@fullcalendar/core/internal';

@Component({
  selector: 'app-importallomany',
  templateUrl: './importallomany.component.html',
  styleUrls: ['./importallomany.component.scss'],
})
export class ImportallomanyComponent implements OnInit {

  excelData: any[];
  protected readonly ColumnMode = ColumnMode;
  protected readonly SortType = SortType;
  protected readonly SelectionType = SelectionType;
  allomany
  overwrite = false
  generateKeycloakImport = false
  cdis = false;
  uploading = false;

  constructor(private http: HttpClient, private data: DataService) { }

  ngOnInit() { }

  private text(value: any): string {
    return String(value ?? '').trim();
  }

  private splitSemicolon(value: any): any {
    const text = this.text(value);
    return text.includes(";")
      ? text.split(";").map((part) => part.trim()).filter(Boolean)
      : value;
  }

  private getStableAdid(row: any): string {
    const kas = this.text(row["KAS belépés"]);
    if (kas) return kas;

    const email = this.text(row["E-mail cím"]);
    if (email && email.includes("@")) return email.toLowerCase();

    const oktazon = this.text(row["Oktazon"]);
    if (oktazon) return `KIR-${oktazon}`;

    return "TEMP-" + uuidv4();
  }

  private resolveFeladatellatas(row: any, fallbackIntezmenyId?: string): string {
    const raw = this.text(row["Feladatellátási hely"] || row["Kiemelt feladatellátási hely"]);
    const kirMatch = raw.match(/^(\d{3})\s*-/);
    if (kirMatch && this.text(row["OM azonosító"])) {
      const resolved = `${row["OM azonosító"]}/${kirMatch[1]}`;
      const fallback = this.text(fallbackIntezmenyId);
      if (fallback && fallback.split("/")[0] === String(row["OM azonosító"]).trim()) {
        return fallback;
      }
      return resolved;
    }
    return raw;
  }

  private toDbInt(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const match = String(value).match(/\d+/);
    if (!match) return null;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private normalizeAllampolgarsag(value: any): string | null {
    const normalized = this.text(value)
      .split(/[;,]/)[0]
      .trim();

    return normalized ? normalized.slice(0, 6) : null;
  }

  private truncateText(value: any, maxLength: number): string | null {
    const normalized = this.text(value);
    return normalized ? normalized.slice(0, maxLength) : null;
  }

  private birthDatePassword(value: any): string {
    const text = this.text(value);
    if (!text) return "Zenit123";

    const formatted = moment(text, ["YYYY-MM-DD", "YYYY. MM. DD.", "YYYY.MM.DD.", "YYYY.MM.DD"], true);
    const parsed = formatted.isValid() ? formatted : moment(text);
    return parsed.isValid() ? parsed.format("YYYYMMDD") : "Zenit123";
  }

  private keycloakUsername(row: any, fallback: string): string {
    const email = this.text(row["E-mail cím"]);
    return email || fallback;
  }

  private downloadKeycloakUsers(rows: any[], fileName: string, role: string) {
    const users = rows
      .map((row) => ({
        username: row.username,
        enabled: true,
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email || undefined,
        attributes: { jogkor: [role], locale: ["hu"] },
        credentials: [{ type: "password", value: row.password, temporary: true }],
        groups: [role],
        realmRoles: [role, "default-roles-master"],
      }))
      .filter((row) => row.username);

    const blob = new Blob([JSON.stringify({ users }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async uploadData() {
    this.uploading = true;
    this.data.showLoading("Állományok ellenőrzése és feltöltés...")
    const sajatIntezmeny = await this.data.getFromStorage("sajatIntezmeny");
    let kuld = [];
    kuld["allomany"] = [];
    kuld["allomany_feladatellatas"] = [];
    kuld["kinevezes"] = [];
    const keycloakUsers = [];
    for await(let t of this.excelData){
      let adid = this.getStableAdid(t);
      const feladatellatas = this.resolveFeladatellatas(t, sajatIntezmeny?.intezmenyId);
      const keycloakUsername = this.keycloakUsername(t, adid);
      kuld["allomany"].push({
        adid: adid,
        oktazon: t["Oktazon"],
        viselt_vez: t["Viselt név vezetéknév"],
        viselt_ker: t["Viselt név keresztnév"],
        szul_vez: t["Születési név vezetéknév"],
        szul_ker: t["Születési név keresztnév"],
        anya_vez: t["Anyja neve vezetéknév"],
        anya_ker: t["Anyja neve keresztnév"],
        viselt_elo: t["Viselt név előtag"],
        szul_elo: t["Születési név előtag"],
        anya_elo: t["Anyja neve előtag"],
        szul_datum: t["Születési dátum"],
        szul_hely: t["Születési hely"],
        szul_orszag: t["Születési ország"],
        allampolgar1: this.normalizeAllampolgarsag(t["1. állampolgárság"]),
        allampolgar2: this.normalizeAllampolgarsag(t["2. állampolgárság"]),
        nem: t["Nem"] == "nő" ? "0" : "1",
        vegzettseg: this.truncateText(t["Végzettség szintje"], 20),
        all_cim_irsz: this.toDbInt(t["Állandó lakcím irányítószám"]),
        all_cim_telepules: t["Állandó lakcím település"],
        all_cim_kozter: t["Állandó lakcím közterület név"],
        all_cim_kozter_jelleg: t["Állandó lakcím közterület jelleg"],
        all_cim_hsz: t["Állandó lakcím házszám"],
        all_cim_pontositas: t["Állandó lakcím pontosítás"],
        tart_cim_irsz: this.toDbInt(t["Tartózkodási cím irányítószám"]),
        tart_cim_telepules: t["Tartózkodási cím település"],
        tart_cim_kozter: t["Tartózkodási cím közterület név"],
        tart_cim_kozter_jelleg: t["Tartózkodási cím közterület jelleg"],
        tart_cim_hsz: this.toDbInt(t["Tartózkodási cím házszám"]),
        tart_cim_pontositas: t["Tartózkodási cím pontosítás"],
        email: t["E-mail cím"],
        telefonszam: t["Telefonszám"],
        taj: t["TAJ"],
        adoazon: t["Adóazonosító"],
        ig_tipus: t["Igazolvány típus"],
        ig_szam: t["Igazolvány szám"]
      });
      kuld["allomany_feladatellatas"].push({
        allomany: adid,
        feladatellatas: feladatellatas,
        fogl_jel: t["Foglalkoztatás jellege"],
        munkakor: t["Munkakör"],
        jogkor: '["pedagogus"]',
        ttf: 1,
        kulso_link: 0,
        hatlan_szerz: t["Határozatlan idejű munkaszerződés"] == "x" ? 1 : 0,
        telefonszam: t["Telefonszám"],
        email: t["E-mail cím"],
        reszm_arany: t["Részmunkaidő aránya"],
        jogviszony_kezd: t["Jogviszony létrejötte"],
        jogviszony_vege: t["Jogviszony megszűnte"],
        rlbtorzsszam: adid,
      });
      keycloakUsers.push({
        username: keycloakUsername,
        firstName: t["Viselt név keresztnév"],
        lastName: t["Viselt név vezetéknév"],
        email: this.text(t["E-mail cím"]),
        password: this.birthDatePassword(t["Születési dátum"]),
      });

    };
    if (this.generateKeycloakImport) {
      this.downloadKeycloakUsers(keycloakUsers, 'allomany-keycloak-users.json', 'pedagogus');
    }
    console.log(kuld)
    this.http.post("/api/import/allomany", [kuld["allomany"],kuld["allomany_feladatellatas"]]).pipe(retry(3), catchError(e => {        // need to capture the 'e' here...
      this.handleError(e, this.data);   // pass to your function
      return EMPTY;          // and return empty
    })).subscribe({ next: (d: any) => {

      this.data.hideLoading()
      console.log(d)

      if (d?.ok) {
        const inserted = d.allomany?.affectedRows ?? 0;
        const relInserted = d.allomany_feladatellatas?.inserted ?? 0;
        const relUpdated = d.allomany_feladatellatas?.updated ?? 0;
        this.data.sendToast(`Sikeres importálás (${inserted} állomány művelet, ${relInserted} új / ${relUpdated} frissített feladatellátás)`, "success")
      } else {
        this.data.sendToast("Hiba történt a feldolgozás során!", "error")
      }
    }, error: () => this.data.hideLoading() });




  }
  handleError(error: HttpErrorResponse, data) {

    data.hideLoading()

    data.sendToast("Nem sikerült elküldeni a kérést!", "error")
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  onFileChange(event: any) {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const workbook = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      this.excelData = XLSX.utils.sheet_to_json(worksheet, { raw: true, defval: null });
      const oktazonSet = new Set((this.allomany || []).map(item => item.oktazon)); // Create a set of Oktazon values from array A
      if (!this.overwrite)
        this.excelData = this.excelData.filter(item => !oktazonSet.has(item["Oktazon"]));
      this.excelData = this.excelData.filter(item => this.text(item["Feladatellátási hely"] || item["Kiemelt feladatellátási hely"]) != "");
      this.excelData.forEach(t => {
        if (t["Születési dátum"]) t["Születési dátum"] = moment(t["Születési dátum"]).format("YYYY-MM-DD")
        if (t["Jogviszony létrejötte"]) t["Jogviszony létrejötte"] = moment(t["Jogviszony létrejötte"]).format("YYYY-MM-DD")
        if (t["Jogviszony megszűnte"]){ t["Jogviszony megszűnte"] = moment(t["Jogviszony megszűnte"]).format("YYYY-MM-DD") }else{ t["Jogviszony megszűnte"] = null}
        if (this.text(t["Munkakör"]).indexOf(",") != -1) t["Munkakör"] = this.text(t["Munkakör"]).split(",")[0]
        if (this.text(t["Szakképzettségek, végzettségek"]).indexOf(";") != -1) t["Szakképzettségek, végzettségek"] = this.text(t["Szakképzettségek, végzettségek"]).split(";").filter(d => d != " ").map(d => d.split(","))
        t["Egyéb továbbképzések"] = this.splitSemicolon(t["Egyéb továbbképzések"])
        t["Pedagógus szakvizsgák"] = this.splitSemicolon(t["Pedagógus szakvizsgák"])
        t["Pótlék"] = this.splitSemicolon(t["Pótlék"])
      })
    };
    reader.readAsBinaryString(file);
  }



  files: any[] = [];

  /**
   * on file drop handler
   */
  onFileDropped($event) {
    this.prepareFilesList($event);
  }

  /**
   * handle file from browsing
   */
  fileBrowseHandler(event) {

    const inputElement = event.target as HTMLInputElement;
    if (inputElement.files) {
      const files: any = inputElement.files;
      // Now you can handle the files as needed
      this.prepareFilesList(files, event);
    }

  }

  /**
   * Delete file from files list
   * @param index (File index)
   */
  deleteFile(index: number) {
    this.files = [];
    this.excelData = [];
    (<HTMLInputElement>document.getElementById("fileDropRef")).value = null;
    this.cdis = false;
    //this.files.splice(index, 1);
  }

  /**
   * Simulate the upload process
   */
  uploadFilesSimulator(index: number, event) {
    setTimeout(() => {
      if (index === this.files.length) {
        return;
      } else {
        const progressInterval = setInterval(() => {
          if (this.files[index].progress === 100) {
            this.onFileChange(event);
            clearInterval(progressInterval);
            this.uploadFilesSimulator(index + 1, event);
          } else {
            this.files[index].progress += 5;
          }
        }, 20);
      }
    }, 100);
  }

  /**
   * Convert Files list to normal array list
   * @param files (Files List)
   */
  async prepareFilesList(files: Array<any>, event = null) {
    //for (const item of files) {
    this.cdis = true;
    const item = files[0]
    item.progress = 0;
    if (item.name.indexOf(".xls") != -1) {
      this.files[0] = item;
      this.allomany = await firstValueFrom(this.data.getAllomany((await this.data.getFromStorage("sajatIntezmeny")).intezmenyId))
      this.uploadFilesSimulator(0, event);
    } else {
      alert("Csak Excel fájlt lehet feltölteni!");
      this.deleteFile(0)
    }

    //}

  }

  /**
   * format bytes
   * @param bytes (File size in bytes)
   * @param decimals (Decimals point)
   */
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }




}
